import { useEffect } from "react";

/**
 * MediaGuard — normal-user protection for every <img> and <video> on the site.
 *
 * This does NOT stop determined users with DevTools, cache access, or screen
 * recorders — nothing web-facing can. It DOES stop the standard browser
 * interactions that let a casual visitor download or copy our media:
 *   - Right-click "Save image as / Open in new tab" (desktop)
 *   - Long-press "Save Image / Copy / Share" on iOS Safari
 *   - Long-press "Download image" on Android Chrome
 *   - Drag-and-drop images out of the browser window
 *   - The three-dot download entry on the native <video controls> menu
 *
 * Implementation notes:
 *   • CSS handles the passive protection (user-drag, touch-callout, user-select).
 *     See index.css for the global rules.
 *   • This component installs three document-level listeners:
 *       - contextmenu → prevent when the target is an img / video
 *       - dragstart   → prevent when the target is an img / video
 *       - copy        → prevent when the current selection is an img / video
 *     It also walks every <video> node and sets controlsList="nodownload" so
 *     Chrome / Edge hide the download entry, and disables Picture-in-Picture.
 *     A MutationObserver keeps that up-to-date as new videos are rendered.
 *   • The listeners are scoped to media elements ONLY — regular page clicks,
 *     product-card navigation, form fields, keyboard shortcuts, and text
 *     selection remain untouched.
 */
export default function MediaGuard() {
  useEffect(() => {
    const isMediaTarget = (t) =>
      t && (t.tagName === "IMG" || t.tagName === "VIDEO" || t.tagName === "PICTURE" || t.tagName === "SOURCE");

    const onContextMenu = (e) => {
      if (isMediaTarget(e.target)) e.preventDefault();
    };
    const onDragStart = (e) => {
      if (isMediaTarget(e.target)) e.preventDefault();
    };
    const onCopy = (e) => {
      // Block copies whose selection sits ONLY on an image/video (e.g. iOS
      // "Copy" menu). Text copies elsewhere on the page are unaffected.
      if (isMediaTarget(e.target)) e.preventDefault();
    };

    const hardenVideo = (v) => {
      if (!v || v.dataset.mgHardened === "1") return;
      // Chrome/Edge look at controlsList to hide the "..." menu items.
      v.setAttribute("controlsList", "nodownload noplaybackrate noremoteplayback");
      v.setAttribute("disablePictureInPicture", "true");
      v.disablePictureInPicture = true;
      v.dataset.mgHardened = "1";
    };

    // Harden existing videos + watch for new ones the router mounts later.
    document.querySelectorAll("video").forEach(hardenVideo);
    const mo = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === "VIDEO") hardenVideo(n);
          if (n.querySelectorAll) n.querySelectorAll("video").forEach(hardenVideo);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("copy", onCopy);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("copy", onCopy);
      mo.disconnect();
    };
  }, []);

  return null;
}
